import { VariantProps, cva } from 'class-variance-authority';
import { DetailedHTMLProps, ForwardedRef, ReactNode, SelectHTMLAttributes, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { ISelectOption } from '../helpers/forms.types';
import styles from './FloatingSelect.module.scss';

const selectVariants = cva(
    [
        styles['floating-select'],
        'block',
        'w-full h-[58px]',
        'font-medium text-grayscale-900',
        'border border-grayscale-300',
        'rounded-lg pl-[14px] pr-[32px] py-[16px]',
        'placeholder:text-transparent',
        'focus:border-primary focus:outline focus:outline-2 focus:outline-primary/10',
    ],
    {
        variants: {
            invalid: {
                true: ['border-danger-500 outline outline-danger-50 focus:border-danger-500 focus:outline-danger-50'],
                false: [],
            },
            valid: {
                true: [
                    'border-success-500 outline outline-success-50 focus:border-success-500 focus:outline-success-50',
                ],
                false: [],
            },
            disabled: {
                true: ['bg-[#FAFAFA] dark:bg-[#20262D] opacity-70'],
                false: [],
            },
        },
        defaultVariants: {
            disabled: false,
            invalid: false,
            valid: false,
        },
    }
);

export interface FloatingSelectProps
    extends Omit<DetailedHTMLProps<SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>, 'children' | 'size'>,
        VariantProps<typeof selectVariants> {
    ref?: ForwardedRef<HTMLSelectElement>;
    options?: ISelectOption[];
    label: ReactNode;
    labelClassName?: string;
}

const FloatingSelect = forwardRef<HTMLSelectElement, FloatingSelectProps>((props, ref) => {
    const { options = [], label, labelClassName, invalid, valid, disabled, className, ...rest } = props;

    return (
        <div className="relative w-[inherit]">
            <select {...rest} ref={ref} className={twMerge(selectVariants({ invalid, valid, disabled, className }))}>
                {options.map((opt, i) => (
                    <option key={i} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>
            <label
                className={twMerge(
                    styles['floating-label'],
                    'absolute left-[1px] top-[1px] px-[14px] py-[16px] text-grayscale-500 pointer-events-none',
                    invalid && 'text-danger-500',
                    labelClassName
                )}
            >
                {label}
            </label>
        </div>
    );
});

export default FloatingSelect;
